import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Icons, SvgIcon } from '@homeproved/shared/ui';
import { useTheme } from '@material-ui/core';

export interface FeatureProps {
  title: string;
  text: string;
  icon?: string;
  iconSize?: number;
  iconColor?: string;
  isMobile: boolean;
  gradient?: boolean;
  isActive?: boolean;
}

const Wrapper = styled(({ isMobile, isActive, ...restProps }) => <div {...restProps} />)`
  padding: 2rem;
  margin-bottom: 2rem;
  margin-right: ${({ isMobile }) => !isMobile && '2rem'};
  position: relative;
  border: ${({ theme }) => `0.1rem solid ${theme.palette.grey['100']}`};
  border-radius: ${({ theme }) => theme.config.defaultBorderRadius};
  background: ${({ isActive, theme }) => !isActive && 'rgba(255, 255, 255, 0.5)'};
  &:first-child {
    border: ${({ theme }) => `0.1rem solid #AD0028`};
  }
`;

const Header = styled(({ isMobile, ...restProps }) => <div {...restProps} />)`
  display: flex;
  align-items: center;

  ${({ isMobile, theme }) =>
    isMobile &&
    `
    border-bottom: 0.1rem solid ${theme.palette.grey['300']};
    padding-bottom: 1rem;
  `};
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.config.fonts.PTSans};
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  padding-top: 0.5rem;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Icon = styled(({ gradient, ...restProps }) => <div {...restProps} />)`
  margin-right: 1.5rem;
  overflow: hidden;

  ${({ gradient }) =>
    gradient &&
    `
    top: 0;
    left: 0;
    width: 3.2rem;
    height: 3.2rem;
    border-radius: 50%;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
  `};
  img {
    max-width: 2.4rem;
  }
`;

const Arrow = styled(({ active, ...restProps }) => <SvgIcon {...restProps} />)`
  margin-right: 0;
  margin-left: auto;

  ${({ active }) =>
    active &&
    `
    transform: rotate(90deg);
    margin-top: 0.5rem;
    margin-right: 0.5rem;
  `};
`;

const Text = styled(({ isMobile, gradient, ...restProps }) => <div {...restProps} />)`
  font-family: ${({ theme }) => theme.config.fonts.PTSans};
  font-size: ${({ isMobile }) => (isMobile ? '1.4rem' : '1.2rem')};
  font-weight: 400;
  line-height: 1.6rem;
  padding: ${({ isMobile, gradient }) =>
    isMobile ? '1rem 0' : gradient ? '0 0 1rem 4.5rem' : '0 0 1rem 3rem'};
`;

export const Feature: FC<FeatureProps> = ({
  title,
  text,
  isActive,
  icon,
  iconSize = 1.5,
  iconColor,
  isMobile,
  gradient = false,
}) => {
  const theme = useTheme();
  const [toggleText, setToggleText] = useState(false);

  return (
    <Wrapper isActive={isActive} isMobile={isMobile}>
      <Header onClick={() => setToggleText(!toggleText)} isMobile={isMobile}>
        <Icon gradient={gradient}>
          {icon ? (
            <img src={isActive ? `./feature-icons/${icon}` : `./feature-icons/lock.png` } alt="" />
          ) : (
            <SvgIcon icon={Icons.STAR_SOLID} size={1.5} color={theme.palette.green.main} />
          )}
        </Icon>
        <Title>{title}</Title>
        {isMobile && (
          <Arrow
            icon={Icons.ANGLE_RIGHT}
            size={1.8}
            color={gradient ? '#fff' : theme.palette.grey['500']}
            active={toggleText}
          />
        )}
      </Header>
      {isMobile && toggleText ? (
        <Text isMobile>{text}</Text>
      ) : !isMobile ? (
        <Text gradient={gradient}>{text}</Text>
      ) : null}
    </Wrapper>
  );
};
